import { AnalyticsEvents } from '../types/events'
import { sanitizeGaEventName } from '../utils/sanitize-ga-event-name.utils'

export type AnalyticsProviderKey = 'googleAnalytics' | 'backendAnalytics' | 'pendoAnalytics'
export type AnalyticsAccess = 'anonymous' | 'authenticated'
export type AnalyticsDeliveryMode = 'immediate' | 'transactional'
export type AnalyticsContextMode = 'none' | 'route'
export type AnalyticsIdentityRequirement = 'none' | 'actor' | 'route-contact'

export interface AnalyticsDeliveryPolicy {
  provider: AnalyticsProviderKey
  eventName: string
  access: AnalyticsAccess
  delivery: AnalyticsDeliveryMode
  context: AnalyticsContextMode
  requiresIdentity: AnalyticsIdentityRequirement
}

/**
 * Catalog **values** that are delivered to GA as-is (the value IS the wire name).
 * Duplicate values from intentional fan-in (e.g. EVENT_RSVP_YES/NO → event_rsvp_event)
 * collapse in the Set — that is expected. Unlisted catalog events still reach GA via
 * sanitizeGaEventName.
 */
const GA_WIRE_EVENTS = new Set<string>([
  AnalyticsEvents.LOGIN_SUCCESS,
  AnalyticsEvents.LOGIN_FAILED,
  AnalyticsEvents.LOGOUT,
  AnalyticsEvents.SIGNUP_COMPLETED,
  AnalyticsEvents.POST_CREATED,
  AnalyticsEvents.POST_EDITED,
  AnalyticsEvents.POST_DELETED,
  AnalyticsEvents.COMMENT_CREATED,
  AnalyticsEvents.COMMENT_EDITED,
  AnalyticsEvents.COMMENT_DELETED,
  AnalyticsEvents.REACTION_ADDED,
  AnalyticsEvents.REACTION_REMOVED,
  AnalyticsEvents.GROUP_JOINED,
  AnalyticsEvents.LESSON_COMPLETED,
  AnalyticsEvents.LESSON_STARTED,
  AnalyticsEvents.EVENT_CREATED,
  AnalyticsEvents.EVENT_RSVP_YES,
  AnalyticsEvents.EVENT_RSVP_NO,
  AnalyticsEvents.MEDIA_UPLOADED,
  AnalyticsEvents.VIDEO_PLAYED,
  AnalyticsEvents.VIDEO_COMPLETED,
  AnalyticsEvents.COURSE_ENROLLED,
  AnalyticsEvents.COURSE_VIEWED,
  AnalyticsEvents.SEARCH_PERFORMED,
  AnalyticsEvents.SEARCH_RESULT_CLICKED,
  AnalyticsEvents.CHAT_MESSAGE_SENT,
  AnalyticsEvents.CHAT_MESSAGE_FAILED,
  AnalyticsEvents.CHAT_BLOCKED,
  AnalyticsEvents.CHAT_CREATED,
  AnalyticsEvents.GOKOLLAB_HOMEPAGE_LAND,
  AnalyticsEvents.GOKOLLAB_LISTING_VIEW,
  AnalyticsEvents.GOKOLLAB_LISTING_JOINED,
  AnalyticsEvents.GOKOLLAB_COMMUNITIES_VIEW,
  AnalyticsEvents.GOKOLLAB_COURSES_VIEW,
  AnalyticsEvents.GOKOLLAB_CREATORS_VIEW,
  AnalyticsEvents.GOKOLLAB_CATEGORY_SWITCH,
  AnalyticsEvents.GOKOLLAB_COURSE_CREATE,
  AnalyticsEvents.GOKOLLAB_COMMUNITY_CREATE,
  AnalyticsEvents.LESSON_OPENED,
  AnalyticsEvents.NEXT_LESSON,
  AnalyticsEvents.COMMUNITY_COURSE_OPENED,
  AnalyticsEvents.FOLLOW,
  AnalyticsEvents.UNFOLLOW,
  AnalyticsEvents.COURSE_MENTIONED,
  AnalyticsEvents.EVENT_MENTIONED,
  AnalyticsEvents.CHANNEL_MENTIONED,
  AnalyticsEvents.GO_LIVE_SESSION_JOINED,
  AnalyticsEvents.USER_BANNED,
  AnalyticsEvents.MEDIA_REMOVED,
  AnalyticsEvents.EVENT_ATTENDEES_VIEWED,
  AnalyticsEvents.EVENT_ATTENDEES_LIST_EXPORTED,
  AnalyticsEvents.MEMBER_VIEWED_OWN_ANSWERS,
  AnalyticsEvents.ADMIN_VIEWED_MEMBER_ANSWERS,
  AnalyticsEvents.CERTIFICATE_DOWNLOAD,
  AnalyticsEvents.EVENT_RSVP,
  AnalyticsEvents.PAGE_VIEW,
  AnalyticsEvents.ELEMENT_CLICKED,
  AnalyticsEvents.ERROR_OCCURRED,
])

const BACKEND_EVENT_NAMES: Record<string, string> = {
  [AnalyticsEvents.USER_AUTO_LOGOUT]: 'client-portal-user-auto-logout',
  [AnalyticsEvents.COMMUNITIES_USER_AUTO_LOGOUT]: 'communities-user-auto-logout',
  [AnalyticsEvents.GENERATE_TOKEN_FAILED]: 'generate-token-failed',
  [AnalyticsEvents.GENERATE_TOKEN_SUCCESS]: 'generate-token-success',
  [AnalyticsEvents.COMMUNITIES_GROUP_ACCESS]: 'communities-group-access-event',
  [AnalyticsEvents.NO_COMMUNITIES_FOUND]: 'no-communities-found',
  [AnalyticsEvents.COURSE_ACCESS_REFRESH]: 'course-access-refresh',
  [AnalyticsEvents.PUSH_NOTIFICATION_INITIALIZED]: 'client-portal-initialize-push-event',
  [AnalyticsEvents.FORCE_UPDATE_EVENT]: 'client-portal-force-app-update-event',
  [AnalyticsEvents.ANALYTICS_CONTEXT_RESOLVER_ERROR]: 'analytics-context-resolver-error',
}

const ANONYMOUS_CAPABLE_GA_EVENTS = new Set<string>([
  AnalyticsEvents.PAGE_VIEW,
  AnalyticsEvents.ELEMENT_CLICKED,
  AnalyticsEvents.ERROR_OCCURRED,
])
const IMMEDIATE_CONTEXT_FREE_GA_EVENTS = new Set<string>([AnalyticsEvents.ELEMENT_CLICKED, AnalyticsEvents.ERROR_OCCURRED])

/**
 * Auto-click fallbacks from convention ids (`page-context-element` → at least three snake segments
 * before `_clicked`). Catalog names like `announcement_course_clicked` / `element_clicked` stay out.
 */
const isElementClickLike = (event: string): boolean => {
  if (event === AnalyticsEvents.ELEMENT_CLICKED) return true
  if (!event.endsWith('_clicked')) return false
  const stem = event.slice(0, -'_clicked'.length)
  return stem.split('_').filter(Boolean).length >= 3
}

export function resolveProviderEventName(provider: AnalyticsProviderKey, event: string): string | null {
  if (provider === 'backendAnalytics') return BACKEND_EVENT_NAMES[event] ?? null
  if (provider === 'pendoAnalytics') return event === AnalyticsEvents.PAGE_VIEW ? event : null
  if (GA_WIRE_EVENTS.has(event)) return event
  return sanitizeGaEventName(event)
}

const delivery = (
  provider: AnalyticsProviderKey,
  eventName: string,
  access: AnalyticsAccess = 'authenticated',
  mode: AnalyticsDeliveryMode = 'transactional',
  context: AnalyticsContextMode = 'route',
  requiresIdentity: AnalyticsIdentityRequirement = access === 'authenticated' ? 'route-contact' : 'none'
): AnalyticsDeliveryPolicy => ({ provider, eventName, access, delivery: mode, context, requiresIdentity })

export function resolveEventDeliveries(event: string): AnalyticsDeliveryPolicy[] {
  if (event === AnalyticsEvents.ANALYTICS_CONTEXT_RESOLVER_ERROR) {
    return [delivery('backendAnalytics', BACKEND_EVENT_NAMES[event]!, 'anonymous', 'immediate', 'none')]
  }

  const gaEventName = resolveProviderEventName('googleAnalytics', event)
  if (!gaEventName) return []

  const deliveries: AnalyticsDeliveryPolicy[] = []
  if (event === AnalyticsEvents.PAGE_VIEW) {
    deliveries.push(delivery('pendoAnalytics', event, 'anonymous'))
  }
  const isImmediateContextFree = IMMEDIATE_CONTEXT_FREE_GA_EVENTS.has(event) || isElementClickLike(event)
  const isAnonymousCapable = ANONYMOUS_CAPABLE_GA_EVENTS.has(event) || isElementClickLike(event)
  deliveries.push(
    delivery(
      'googleAnalytics',
      gaEventName,
      isAnonymousCapable ? 'anonymous' : 'authenticated',
      isImmediateContextFree ? 'immediate' : 'transactional',
      isImmediateContextFree ? 'none' : 'route'
    )
  )

  const backendEventName = resolveProviderEventName('backendAnalytics', event)
  if (backendEventName) deliveries.push(delivery('backendAnalytics', backendEventName))

  return deliveries
}
